/**
 * `/campaigns/templates` route — campaign templates catalogue (feature #60).
 *
 * Exposes:
 *   - `CampaignsTemplatesPage` — the React component rendered at the path.
 *   - `createCampaignsTemplatesRoute(rootRoute)` — factory consumed by the
 *     root router when this route is wired into the tree.
 *
 * The router is programmatic (`../router.tsx` owns tree assembly) and is
 * outside this agent's lane, so we export a factory instead of mutating the
 * tree in-place. Root wire-up will:
 *
 * ```ts
 * import { createCampaignsTemplatesRoute } from "./routes/campaigns.templates";
 * const campaignsTemplatesRoute = createCampaignsTemplatesRoute(rootRoute);
 * const routeTree = rootRoute.addChildren([indexRoute, campaignsTemplatesRoute]);
 * ```
 *
 * No other files are touched from this lane.
 */

import {
	type AnyRoute,
	createRoute,
} from "@tanstack/react-router";

import { TemplatesList } from "../features/campaign-templates/TemplatesList";

export function CampaignsTemplatesPage() {
	return <TemplatesList />;
}

/**
 * Build the route node for `/campaigns/templates`. Called by `router.tsx`
 * when the scaffold registry mounts this lane.
 */
export function createCampaignsTemplatesRoute<TParent extends AnyRoute>(
	parentRoute: TParent,
) {
	return createRoute({
		getParentRoute: () => parentRoute,
		path: "/campaigns/templates",
		component: CampaignsTemplatesPage,
	});
}

/**
 * Canonical URL path for this surface. Exported so root wire-up and linking
 * code can share a single source of truth.
 */
export const CAMPAIGNS_TEMPLATES_PATH = "/campaigns/templates";

export default CampaignsTemplatesPage;
